/**
 * Store Module Dynamic Loading System
 * Enables each store to have its own complete component/page/layout system
 */

// Static import mapping for better Vite compatibility
const storeModuleMap = {
  'default': {
    async index() { return import('../stores/default/index.js'); },
    async components() { throw new Error('No components for default store'); },
    async pages() { throw new Error('No pages for default store'); },
    async layouts() { throw new Error('No layouts for default store'); },
    async hooks() { throw new Error('No hooks for default store'); },
    async utils() { throw new Error('No utils for default store'); },
    async data() { throw new Error('No data for default store'); },
  },
  'antic': {
    async index() { return import('../stores/antic/index.js'); },
    async components() { return import('../stores/antic/components/index.js'); },
    async pages() { return import('../stores/antic/pages/index.js'); },
    async layouts() { return import('../stores/antic/layouts/index.js'); },
    async hooks() { return import('../stores/antic/hooks/index.js'); },
    async utils() { throw new Error('No utils for antic store'); },
    async data() { return import('../stores/antic/data/index.js'); },
  }
};

// Core system module mapping
const coreModuleMap = {
  async components() { return import('../components/index.js'); },
  async pages() { return import('../pages/index.js'); },
  async layouts() { return import('../layouts/index.js'); },
  async hooks() { throw new Error('No core hooks'); },
  async utils() { throw new Error('No core utils'); },
  async data() { throw new Error('No core data'); },
};

/**
 * Dynamically load store-specific modules with fallback to core system
 * @param {string} storeId - Store identifier (e.g., 'antic', 'default')
 * @param {string} moduleType - Module type ('components', 'pages', 'layouts', 'hooks', 'utils')
 * @returns {Promise<Object>} - Loaded module or core fallback
 */
export const loadStoreModule = async (storeId, moduleType) => {
  console.log(`🔄 Attempting to load store module: ${storeId}/${moduleType}`);
  
  // Try store-specific module first
  try {
    if (storeModuleMap[storeId] && storeModuleMap[storeId][moduleType]) {
      const storeModule = await storeModuleMap[storeId][moduleType]();
      console.log(`✅ Loaded store-specific ${moduleType} for ${storeId}`);
      
      return {
        ...storeModule,
        isStoreSpecific: true,
        storeId,
        moduleType
      };
    } else {
      throw new Error(`No ${moduleType} mapping for store ${storeId}`);
    }
  } catch (storeError) {
    console.log(`⚠️ No store-specific ${moduleType} found for ${storeId}, falling back to core`);
    
    // Fallback to core system modules
    try {
      if (coreModuleMap[moduleType]) {
        const coreModule = await coreModuleMap[moduleType]();
        console.log(`✅ Loaded core ${moduleType} as fallback`);
        
        return {
          ...coreModule,
          isStoreSpecific: false,
          storeId: 'core',
          moduleType
        };
      } else {
        throw new Error(`No core ${moduleType} available`);
      }
    } catch (coreError) {
      console.error(`❌ Failed to load both store and core ${moduleType}:`, coreError);
      throw new Error(`Module ${moduleType} not found for store ${storeId} or core system`);
    }
  }
};

/**
 * Load multiple store modules in parallel
 * @param {string} storeId - Store identifier
 * @param {string[]} moduleTypes - Array of module types to load
 * @returns {Promise<Object>} - Object with loaded modules
 */
export const loadStoreModules = async (storeId, moduleTypes) => {
  console.log(`🔄 Loading multiple modules for store: ${storeId}`);
  
  const modulePromises = moduleTypes.map(async (moduleType) => {
    const module = await loadStoreModule(storeId, moduleType);
    return [moduleType, module];
  });
  
  const moduleResults = await Promise.all(modulePromises);
  const modules = Object.fromEntries(moduleResults);
  
  console.log(`✅ Loaded ${moduleTypes.length} modules for ${storeId}:`, Object.keys(modules));
  
  return modules;
};

/**
 * Check if store has specific module implementation
 * @param {string} storeId - Store identifier
 * @param {string} moduleType - Module type to check
 * @returns {Promise<boolean>} - True if store has specific implementation
 */
export const hasStoreModule = async (storeId, moduleType) => {
  try {
    if (storeModuleMap[storeId] && storeModuleMap[storeId][moduleType]) {
      await storeModuleMap[storeId][moduleType]();
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

/**
 * Get store capabilities (which modules it has implemented)
 * @param {string} storeId - Store identifier
 * @returns {Promise<Object>} - Store capabilities object
 */
export const getStoreCapabilities = async (storeId) => {
  const moduleTypes = ['components', 'pages', 'layouts', 'hooks', 'utils', 'data'];
  
  const capabilities = {};
  
  for (const moduleType of moduleTypes) {
    capabilities[moduleType] = await hasStoreModule(storeId, moduleType);
  }
  
  console.log(`📊 Store ${storeId} capabilities:`, capabilities);
  
  return {
    storeId,
    capabilities,
    autonomyLevel: Object.values(capabilities).filter(Boolean).length,
    isFullyAutonomous: Object.values(capabilities).every(Boolean)
  };
};

/**
 * Enhanced theme loading with store module support
 * @param {string} storeId - Store identifier
 * @returns {Promise<Object>} - Enhanced theme object with modules
 */
export const loadEnhancedTheme = async (storeId) => {
  console.log(`🎨 Loading enhanced theme for store: ${storeId}`);
  
  try {
    // Load base theme (tokens, config, stores) using static mapping
    let baseTheme;
    if (storeModuleMap[storeId] && storeModuleMap[storeId].index) {
      baseTheme = await storeModuleMap[storeId].index();
    } else {
      throw new Error(`No theme mapping found for store: ${storeId}`);
    }
    
    // Get store capabilities
    const storeCapabilities = await getStoreCapabilities(storeId);
    
    // Load available modules
    const availableModules = Object.entries(storeCapabilities.capabilities)
      .filter(([_, hasModule]) => hasModule)
      .map(([moduleType]) => moduleType);
    
    let storeModules = {};
    if (availableModules.length > 0) {
      storeModules = await loadStoreModules(storeId, availableModules);
    }
    
    const enhancedTheme = {
      ...baseTheme,
      id: storeId,
      capabilities: storeCapabilities,
      modules: storeModules,
      isEnhanced: true
    };
    
    console.log(`✅ Enhanced theme loaded for ${storeId}:`, enhancedTheme);
    
    return enhancedTheme;
  } catch (error) {
    console.error(`❌ Failed to load enhanced theme for ${storeId}:`, error);
    throw error;
  }
};
